import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import { FaEdit, FaInfoCircle } from "react-icons/fa";
import "../../assets/css/DeliverySummary.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RouteMap } from "../RouteMap";
import { formatNumber } from "../../utils/helpers";
import SpecialItemsSummary from "./SpecialItemsSummary";

export default function DeliverySummary({
  formState,
  onSubmit,
  onEditRoute,
  onEditItems,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { transportType } = location.state || {};
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [emailErrorKey, setEmailErrorKey] = useState("");
  const [deliveryAs, setDeliveryAs] = useState("personal");

  // ✅ حالات جديدة للسعر ومدة الدفع
  const [priceOffer, setPriceOffer] = useState("");
  const [isPriceNegotiable, setIsPriceNegotiable] = useState(false);
  const [paymentTerm, setPaymentTerm] = useState("");
  const [paymentTermError, setPaymentTermError] = useState("");

  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  // ── خيارات مدة الدفع ────────────────────────────────────────────────────
  const PAYMENT_TERM_OPTIONS = [
    { value: "on_delivery", label: t("delivery_summary.paymentTerms.onDelivery", "Bei Lieferung") },
    { value: "14_days", label: t("delivery_summary.paymentTerms.after14Days", "14 Tage nach Lieferung") },
    { value: "3_days", label: t("delivery_summary.paymentTerms.after3Days", "3 Tage nach Lieferung") },
    { value: "30_days", label: t("delivery_summary.paymentTerms.after30Days", "30 Tage nach Lieferung") },
    { value: "7_days", label: t("delivery_summary.paymentTerms.after7Days", "7 Tage nach Lieferung") },
    { value: "by_agreement", label: t("delivery_summary.paymentTerms.byAgreement", "Nach Vereinbarung") },
  ];

  const validateEmail = () => {
    if (userType) return true;

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailErrorKey("delivery_summary.email_required");
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailErrorKey("delivery_summary.invalid_email");
      return false;
    }

    setEmailErrorKey("");
    return true;
  };

  const handleSubmit = async () => {
    // ✅ التحقق من الإيميل
    if (!validateEmail()) return;

    // ✅ التحقق من مدة الدفع (مطلوبة)
    if (!paymentTerm) {
      setPaymentTermError(
        t("delivery_summary.paymentTermRequired", "Bitte wählen Sie ein Zahlungsziel aus.")
      );
      // التمرير إلى حقل مدة الدفع
      document
        .getElementById("payment-term-section")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setPaymentTermError("");

    setLoading(true);
    try {
      const finalData = {
        ...formState,
        email: !userType ? email.trim() : undefined,
        deliveryAs: deliveryAs,
        // ✅ إضافة البيانات الجديدة
        price_offer: priceOffer ? parseFloat(priceOffer) : null,
        is_price_negotiable: isPriceNegotiable,
        payment_term: paymentTerm,
      };
      await onSubmit(finalData);
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    const handler = () => {
      setUserType(localStorage.getItem("userType"));
    };

    if (formState?.price_offer) {
      setPriceOffer(String(formState.price_offer));
    }
    
    if (formState?.is_price_negotiable !== undefined) {
      // تحويل القيمة (قد تكون "1", "0", true, false)
      setIsPriceNegotiable(
        formState.is_price_negotiable === "1" ||
        formState.is_price_negotiable === 1 ||
        formState.is_price_negotiable === true
      );
    }
    
    if (formState?.payment_term) {
      setPaymentTerm(formState.payment_term);
    }
    window.addEventListener("userTypeChanged", handler);

    const storageHandler = (e) => {
      if (e.key === "userType") handler();
    };
    window.addEventListener("storage", storageHandler);

    return () => {
      window.removeEventListener("userTypeChanged", handler);
      window.removeEventListener("storage", storageHandler);
    };
  }, [formState?.price_offer, formState?.is_price_negotiable, formState?.payment_term]);

  const handleEditRoute = () => {
    if (onEditRoute) {
      onEditRoute();
    } else {
      navigate(-2);
    }
  };

  const handleEditItems = () => {
    if (onEditItems) {
      onEditItems();
    } else {
      navigate(-1);
    }
  };

  const parseCoordinates = (coordString) => {
    if (!coordString) return null;
    try {
      const [lat, lng] = coordString
        .split(",")
        .map((coord) => parseFloat(coord.trim()));
      return [lat, lng];
    } catch (error) {
      console.error("Error parsing coordinates:", error);
      return null;
    }
  };

  const fromCoords = parseCoordinates(formState.from_lat_long);
  const toCoords = parseCoordinates(formState.to_lat_long);

  return (
    <Container className="py-4">
      <div className="summary-header">
        <div>
          <div className="summary-title">{t("delivery_summary.title")}</div>
          <div className="small-muted">{t(transportType?.label)}</div>
        </div>
      </div>

      <Row className="g-3">
        <Col lg={8}>
          {/* ═══════════════════════════════════════════════════════════════
              Route & Details Card
             ═══════════════════════════════════════════════════════════════ */}
          <Card className="card-map mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="small-muted">
                    {t("delivery_summary.route_and_data")}
                  </h6>
                  <p className="strong">
                    {t("delivery_summary.pickup")}: {formState.booking_from}
                  </p>
                  <p className="strong">
                    {t("delivery_summary.delivery")}: {formState.booking_to}
                  </p>

                  <div className="dates mt-3">
                    <div className="small-muted">
                      {t("delivery_summary.pickup_dates")}
                    </div>
                    <div className="strong">
                      {formState.pickupStartDate}{" "}
                      {formState.pickupStartDate &&
                        formState.pickupEndDate &&
                        "-"}{" "}
                      {formState.pickupEndDate}
                    </div>
                    <div className="small-muted mt-2">
                      {t("delivery_summary.time")}
                    </div>
                    <div className="strong">
                      {formState.pickupStartTime}{" "}
                      {formState.pickupStartTime &&
                        formState.pickupEndTime &&
                        "-"}{" "}
                      {formState.pickupEndTime}
                    </div>

                    <div className="small-muted mt-2">
                      {t("delivery_summary.delivery_dates")}
                    </div>
                    <div className="strong">
                      {formState.deliveryStartDate}{" "}
                      {formState.deliveryStartDate &&
                        formState.deliveryEndDate &&
                        "-"}{" "}
                      {formState.deliveryEndDate}
                    </div>
                    <div className="small-muted mt-2">
                      {t("delivery_summary.time")}
                    </div>
                    <div className="strong">
                      {formState.deliveryStartTime}{" "}
                      {formState.deliveryStartTime &&
                        formState.deliveryEndTime &&
                        "-"}{" "}
                      {formState.deliveryEndTime}
                    </div>
                  </div>
                </div>
                <FaEdit
                  className="edit-icon"
                  style={{ cursor: "pointer" }}
                  onClick={handleEditRoute}
                  title={t("delivery_summary.edit_route")}
                />
              </div>
            </Card.Body>
          </Card>

          {/* ═══════════════════════════════════════════════════════════════
              Item Details Card
             ═══════════════════════════════════════════════════════════════ */}
          <Card className="mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="small-muted">
                    {t("delivery_summary.item_details")}
                  </h6>
                  {formState.details?.items &&
                    Array.isArray(formState.details.items) &&
                    formState.details.items.map((item, index) => (
                      <div key={index} className="mt-2">
                        <div className="strong">{t(item.item_type)}</div>
                        <div className="small-muted">
                          {t("delivery_summary.quantity")}: {item.num_items}
                          {item.item_weight &&
                            ` | ${t("delivery_summary.weight")}: ${formatNumber(item.item_weight)} kg`}
                          {item.total_weight &&
                            ` | ${t("delivery_summary.weight")}: ${formatNumber(item.total_weight)} kg`}
                          {item.dimensions &&
                            ` | ${t("delivery_summary.dimensions")}: ${item.dimensions}`}
                          {item.board_number &&
                            ` | ${t("delivery_summary.board_number")}: ${item.board_number}`}
                          {item.making_year &&
                            ` | ${t("delivery_summary.making_year")}: ${item.making_year}`}
                          {item.driving &&
                            ` | ${t("delivery_summary.driving")}: ${item.driving === "1" ? t("dynamic_form.driving_yes") : t("dynamic_form.driving_no")}`}
                        </div>
                      </div>
                    ))}
                  {formState.details?.special_items && (
                    <SpecialItemsSummary data={formState.details.special_items} />
                  )}
                  {(!formState.details?.items ||
                    !Array.isArray(formState.details.items) ||
                    formState.details.items.length === 0) &&
                    !formState.details?.special_items && (
                    <div className="text-muted">
                      {t("delivery_summary.no_items")}
                    </div>
                  )}
                </div>
                <FaEdit
                  className="edit-icon"
                  style={{ cursor: "pointer" }}
                  onClick={handleEditItems}
                  title={t("delivery_summary.edit_items")}
                />
              </div>
            </Card.Body>
          </Card>

          {/* ═══════════════════════════════════════════════════════════════
              ✅ NEW: Preisvorstellung (Price Offer) — Optional
             ═══════════════════════════════════════════════════════════════ */}
          <Card className="details-card mb-4 price-offer-card">
            <div className="details-header">
              <h6 className="small-muted mb-0">
                {t("delivery_summary.priceOffer.title", "Preisvorstellung")}{" "}
                <span className="text-muted fw-normal">
                  ({t("delivery_summary.optional", "optional")})
                </span>
                <FaInfoCircle
                  className="ms-2 text-muted"
                  size={12}
                  title={t(
                    "delivery_summary.priceOffer.hint",
                    "Ihre Preisvorstellung hilft Transportunternehmen bei der Angebotserstellung."
                  )}
                />
              </h6>
            </div>
            <div className="details-list">
              <p className="text-muted small mb-3">
                {t(
                  "delivery_summary.priceOffer.hint",
                  "Ihre Preisvorstellung hilft Transportunternehmen bei der Angebotserstellung."
                )}
              </p>

              <Row className="g-3 align-items-center">
                <Col xs={12} md={6}>
                  <div className="price-input-wrapper">
                    <span className="price-currency">€</span>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={priceOffer}
                      onChange={(e) => {
                        setPriceOffer(e.target.value);
                        // إذا تم مسح السعر، إلغاء تفعيل الـ checkbox
                        if (!e.target.value) setIsPriceNegotiable(false);
                      }}
                      className="price-input"
                    />
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <Form.Check
                    type="checkbox"
                    id="price-negotiable"
                    label={
                      <span className="d-flex align-items-center gap-1">
                        {t(
                          "delivery_summary.priceOffer.negotiable",
                          "Preis ist verhandelbar"
                        )}
                        <FaInfoCircle
                          className="text-muted"
                          size={11}
                          title={t(
                            "delivery_summary.priceOffer.negotiableHint",
                            "Aktivieren Sie diese Option, wenn der Preis verhandelbar ist."
                          )}
                        />
                      </span>
                    }
                    // checked={formState.is_price_negotiable}
                    checked={isPriceNegotiable}
                    disabled={!priceOffer}                     
                    onChange={(e) => setIsPriceNegotiable(e.target.checked)}
                    className="price-negotiable-check"
                  />
                </Col>
              </Row>
            </div>
          </Card>

          {/* ═══════════════════════════════════════════════════════════════
              ✅ NEW: Zahlungsziel (Payment Term) — Required
             ═══════════════════════════════════════════════════════════════ */}
          <Card
            className="details-card mb-4 payment-term-card"
            id="payment-term-section"
          >
            <div className="details-header">
              <h6 className="small-muted mb-0">
                {t("delivery_summary.paymentTerm.title", "Zahlungsziel")}
                <span className="text-danger ms-1">*</span>
                <FaInfoCircle
                  className="ms-2 text-muted"
                  size={12}
                  title={t(
                    "delivery_summary.paymentTerm.hint",
                    "Wann möchten Sie die Zahlung leisten?"
                  )}
                />
              </h6>
            </div>
            <div className="details-list">
              <p className="text-muted small mb-3">
                {t(
                  "delivery_summary.paymentTerm.hint",
                  "Wann möchten Sie die Zahlung leisten?"
                )}
              </p>

              <Row className="g-2">
                {PAYMENT_TERM_OPTIONS.map((option) => (
                  <Col xs={12} md={6} key={option.value}>
                    <Form.Check
                      type="radio"
                      id={`payment-term-${option.value}`}
                      name="payment_term"
                      label={option.label}
                      value={option.value} 
                      checked={paymentTerm === option.value} 
                      onChange={(e) => {
                        setPaymentTerm(e.target.value);
                        setPaymentTermError("");
                      }}
                      className="payment-term-radio"
                    />
                  </Col>
                ))}
              </Row>

              {paymentTermError && (
                <div className="text-danger small mt-3">
                  {paymentTermError}
                </div>
              )}
            </div>
          </Card>

          {/* ═══════════════════════════════════════════════════════════════
              Additional Details Card
             ═══════════════════════════════════════════════════════════════ */}
          <Card className="details-card mb-4">
            <div className="details-header">
              <h6 className="small-muted">
                {t("delivery_summary.additional_details")}
              </h6>
            </div>
            <div className="details-list">
              {formState.details?.fragile === "1" && (
                <div className="detail-row">
                  <div>{t("delivery_summary.fragile")}</div>
                  <div className="small-muted">
                    {formState.details.fragileDetails}
                  </div>
                </div>
              )}

              {formState.details?.specialHandling === "1" && (
                <div className="detail-row">
                  <div>{t("delivery_summary.special_handling")}</div>
                  <div className="small-muted">
                    {formState.details.handlingDetails}
                  </div>
                </div>
              )}

              {formState.details?.helpCarry &&
                formState.details.helpCarry !== "keine" && (
                  <div className="detail-row">
                    <div>{t("delivery_summary.help_carry")}</div>
                    <div className="small-muted">
                      {formState.details.helpCarry === "0"
                        ? t("delivery_summary.no_help")
                        : formState.details.helpCarry === "1"
                          ? t("delivery_summary.partial_help")
                          : formState.details.helpCarry === "2"
                            ? t("delivery_summary.full_help")
                            : formState.details.helpCarry}
                    </div>
                  </div>
                )}

              {formState.details?.beladungFloor != null &&
                formState.details.helpCarry !== "0" && (
                  <div className="detail-row mt-2">
                    <div>
                      {t("delivery_summary.pickup_floor")}:{" "}
                      <strong>{formState.details.beladungFloor}</strong>
                    </div>
                    <div className="small-muted">
                      {t("delivery_summary.elevator")}:{" "}
                      <strong>
                        {formState.details.liftBeladung === "1"
                          ? t("delivery_summary.yes")
                          : t("delivery_summary.no")}
                      </strong>
                    </div>
                  </div>
                )}

              {formState.details?.entladungFloor != null &&
                formState.details.helpCarry !== "0" && (
                  <div className="detail-row mt-2">
                    <div>
                      {t("delivery_summary.delivery_floor")}:{" "}
                      <strong>{formState.details.entladungFloor}</strong>
                    </div>
                    <div className="small-muted">
                      {t("delivery_summary.elevator")}:{" "}
                      <strong>
                        {formState.details.liftEntladung === "1"
                          ? t("delivery_summary.yes")
                          : t("delivery_summary.no")}
                      </strong>
                    </div>
                  </div>
                )}

              {formState.details?.description && (
                <div className="detail-row mt-2">
                  <div>{t("delivery_summary.additional_notes")}</div>
                  <div className="small-muted">
                    {formState.details.description}
                  </div>
                </div>
              )}
            </div>

            {formState.details?.images &&
              formState.details.images.length > 0 && (
                <div className="mt-4">
                  <Row className="g-2">
                    {formState.details.images.map((image, index) => (
                      <Col
                        xs={6}
                        md={4}
                        key={index}
                        className="mb-2 position-relative"
                      >
                        {image instanceof File ? (
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`img ${index + 1}`}
                            className="img-fluid rounded"
                            style={{
                              width: "100%",
                              height: "120px",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <img
                            src={image}
                            alt={`img ${index + 1}`}
                            className="img-fluid rounded"
                            style={{
                              width: "100%",
                              height: "120px",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              console.error("Error loading image:", image);
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
          </Card>
        </Col>

        {/* ═════════════════════════════════════════════════════════════════
            Right Sidebar
           ═════════════════════════════════════════════════════════════════ */}
        <Col lg={4}>
          <Card className="card-map mb-4">
            <RouteMap fromCoords={fromCoords} toCoords={toCoords} />
          </Card>

          <Card className="details-card mb-4">
            <div className="details-header">
              <h6 className="small-muted">
                {t("delivery_summary.your_information")}
              </h6>
            </div>
            <div className="details-list">
              {!userType && (
                <Form.Group className="mb-3">
                  <Form.Label>{t("delivery_summary.email")}</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailErrorKey) setEmailErrorKey("");
                    }}
                    placeholder={t("delivery_summary.email_placeholder")}
                    isInvalid={!!emailErrorKey}
                  />
                  <Form.Control.Feedback type="invalid">
                    {emailErrorKey ? t(emailErrorKey) : ""}
                  </Form.Control.Feedback>
                </Form.Group>
              )}

              <Form.Group>
                <Form.Label>{t("delivery_summary.listing_as")}</Form.Label>
                <Form.Select
                  value={deliveryAs}
                  onChange={(e) => setDeliveryAs(e.target.value)}
                >
                  <option value="personal">
                    {t("delivery_summary.personal")}
                  </option>
                  <option value="company">
                    {t("delivery_summary.company")}
                  </option>
                </Form.Select>
              </Form.Group>
            </div>
          </Card>

          <Button
            type="button"
            variant="warning"
            className="w-100 mt-3"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t("delivery_summary.submitting")}
              </>
            ) : (
              t("delivery_summary.submit")
            )}
          </Button>

          <Button
            variant="secondary"
            className="px-4 rounded-5 w-100 mt-3"
            onClick={handleEditItems}
          >
            {t("delivery_summary.back")}
          </Button>
        </Col>
      </Row>
    </Container>
  );
}